import { SuretyAccountId, SuretyQuoteId } from "@inrev/common";
import { SuretyAccount, useFetchSuretyAccount } from "@portal/api/account";
import { BondRequest, useFetchBondRequest } from "@portal/api/bond-request/agent";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { useRecentlyViewedEntities } from "@portal/hooks/useRecentlyViewed";
import { createContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AcceptedBondRequestView } from "./status/accepted/AcceptedBondRequestView";
import { ApprovedBondRequestView } from "./status/approved/ApprovedBondRequestView";
import { ClosedBondRequestView } from "./status/closed/ClosedBondRequestView";
import { DeclinedBondRequestView } from "./status/declined/DeclinedBondRequestView";
import { PendingBondRequestView } from "./status/pending/PendingBondRequestView";
import { ReviewBondRequestView } from "./status/review/ReviewBondRequestView";

export const BondRequestContext = createContext<BondRequest>(undefined!);

const getView = (request: BondRequest, account: SuretyAccount) => {
	switch (request.status) {
		case "approved":
			return <ApprovedBondRequestView request={request} account={account} />;
		case "review":
			return <ReviewBondRequestView request={request} account={account} />;
		case "declined":
			return <DeclinedBondRequestView request={request} account={account} />;
		case "accepted":
			return <AcceptedBondRequestView request={request} account={account} />;
		case "pending":
			return <PendingBondRequestView request={request} account={account} />;
		case "closed":
			return <ClosedBondRequestView request={request} account={account} />;
		default:
			return null;
	}
};

export const BondRequestView = () => {
	const { accountId, id } = useParams();
	if (!accountId || !id) throw new Error("Missing route params: accountId, id");
	const { bondRequest, bondRequestLoading, bondRequestError } = useFetchBondRequest(
		accountId as SuretyAccountId,
		id as SuretyQuoteId,
	);
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError } = useFetchSuretyAccount(
		accountId as SuretyAccountId,
	);
	const navigate = useNavigate();
	const { addRecentlyViewedEntity } = useRecentlyViewedEntities();

	useEffect(() => {
		if (bondRequest !== undefined && bondRequest.status === "bound") {
			navigate(`/bonds/${bondRequest.bondId}`, { replace: true });
		}
	}, [bondRequest, navigate]);

	useEffect(() => {
		if (bondRequest) {
			addRecentlyViewedEntity({ id: bondRequest.id, entityType: "bondRequest" });
		}
	}, [bondRequest, addRecentlyViewedEntity]);

	useEffect(() => {
		if (bondRequestError || suretyAccountError) {
			navigate("/requests");
		}
	}, [bondRequestError, suretyAccountError, navigate]);

	if (
		bondRequestLoading ||
		suretyAccountIsLoading ||
		bondRequest === undefined ||
		bondRequest.status === "bound" ||
		suretyAccount === undefined
	)
		return <LoadingModal />;

	return (
		<BondRequestContext.Provider value={bondRequest}>
			{getView(bondRequest, suretyAccount)}
		</BondRequestContext.Provider>
	);
};
